
import React, { useState, useEffect } from 'react';
import { generateSurgicalReport, generatePostOpPlan } from '../services/geminiService';
import { PatientInfo, ReportData, User, LabEntry, HospitalLocation, Inpatient, SurgicalLog, OnCallCaseLog, ReportExample } from '../types';
import { dbService } from '../lib/firebase';
import { PROCEDURES_LIST } from '../constants';
import { FileText, Wand2, Copy, Save, Loader2, ClipboardCheck, Stethoscope, Users, FlaskConical, Check, BedDouble, Activity, Search, BrainCircuit, Sparkles, Hash } from 'lucide-react';

/**
 * Consistent name normalization used across all entry points.
 */
const toTitleCase = (str: string | undefined | null) => {
    if (!str) return '';
    const trimmed = str.trim();
    if (!trimmed) return '';
    return trimmed.toLowerCase().split(/\s+/).map(word => 
        word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
};

export const ReportGenerator: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [generatedReport, setGeneratedReport] = useState('');
  const [copied, setCopied] = useState(false);
  const [learningSuccess, setLearningSuccess] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  
  const [teamInfo, setTeamInfo] = useState({
    date: new Date().toISOString().split('T')[0],
    attending: '',
    resident: '',
    secondResident: '',
    anesthesia: ''
  });

  const [patient, setPatient] = useState<PatientInfo>({
    name: '',
    caseNumber: '',
    species: 'Canine',
    breed: '',
    age: '',
    weight: 0,
    sex: 'MN',
    procedure: '',
    preOpDiagnosis: '',
    postOpDiagnosis: ''
  });
  
  const [surgicalDetails, setSurgicalDetails] = useState({
    findings: '',
    complications: '',
    implants: '',
    closure: ''
  });

  const [labForm, setLabForm] = useState({
      active: false,
      sampleType: 'Histopathology',
      hospital: 'Mission' as HospitalLocation,
      submittedTo: 'Antech'
  });
  const [labSuccess, setLabSuccess] = useState(false);

  const [inpatientForm, setInpatientForm] = useState({
      active: false,
      location: 'Mission' as HospitalLocation,
      pointOfContact: '',
      notes: ''
  });
  const [inpatientSuccess, setInpatientSuccess] = useState(false);
  const [isGeneratingNotes, setIsGeneratingNotes] = useState(false);

  const [caseLogForm, setCaseLogForm] = useState({
      active: false,
      hospital: 'MVES' as 'MVES' | 'OPVES',
      res1Cert: false,
      res2Cert: false
  });
  const [caseLogSuccess, setCaseLogSuccess] = useState(false);

  const [onCallForm, setOnCallForm] = useState({
      active: false,
      location: 'MVES' as 'MVES' | 'OPVES',
      referringDoctor: ''
  });
  const [onCallSuccess, setOnCallSuccess] = useState(false);

  useEffect(() => {
      const loadUsers = async () => {
          const fetchedUsers = await dbService.getUsers();
          setUsers(fetchedUsers);
      };
      loadUsers();
  }, []);

  const attendings = Array.from(new Set(users.filter(u => u.role === 'Attending').map(u => u.lastName))).sort();
  const residents = Array.from(new Set(users.filter(u => u.role === 'Resident').map(u => u.lastName))).sort();

  const handleGenerate = async () => {
    if (!patient.procedure || !patient.name) return;
    setLoading(true);
    setCopied(false);
    setLearningSuccess(false);
    
    const examples = await dbService.getRecentExamples(patient.procedure);

    const data: ReportData = {
      ...teamInfo,
      patient,
      ...surgicalDetails
    };

    const report = await generateSurgicalReport(data, examples);
    setGeneratedReport(report);
    setLoading(false);
  };

  const handleAutopopulateStatus = async () => {
    if (!patient.procedure) return;
    setIsGeneratingNotes(true);
    
    const data: ReportData = {
        ...teamInfo,
        patient,
        ...surgicalDetails
    };

    const notes = await generatePostOpPlan(data);
    setInpatientForm(prev => ({ ...prev, notes }));
    setIsGeneratingNotes(false);
  };

  const handleCopyAndSave = async () => {
      if (!generatedReport) return;
      
      navigator.clipboard.writeText(generatedReport);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);

      const newExample: ReportExample = {
          id: `ex-${Date.now()}`,
          procedure: patient.procedure,
          reportText: generatedReport,
          userId: 'current-user',
          timestamp: new Date().toISOString()
      };

      await dbService.saveReportExample(newExample);
      setLearningSuccess(true);
      setTimeout(() => setLearningSuccess(false), 3000);
  };

  const handleAddToLab = async () => {
      if (!patient.name) {
          alert("Please enter a patient name first.");
          return;
      }
      
      const newLab: LabEntry = {
          id: `lab-${Date.now()}`,
          patientName: patient.name,
          sampleType: labForm.sampleType,
          hospital: labForm.hospital,
          submissionDate: teamInfo.date,
          submittedTo: labForm.submittedTo,
          status: 'pending',
          notes: patient.procedure,
          attending: teamInfo.attending,
          resident: teamInfo.resident
      };

      await dbService.saveLab(newLab);
      setLabSuccess(true);
      setTimeout(() => setLabSuccess(false), 3000);
      setLabForm(prev => ({...prev, active: false}));
  };

  const handleAddToInpatient = async () => {
      if (!patient.name) {
          alert("Please enter a patient name first.");
          return;
      }
      
      const signalment = [patient.age, patient.sex, patient.breed].filter(Boolean).join(' ');
      
      const newPatient: Inpatient = {
          id: `pt-${Date.now()}`,
          location: inpatientForm.location,
          name: patient.name,
          signalment: signalment || 'Unknown',
          diagnosis: patient.postOpDiagnosis || patient.preOpDiagnosis || '',
          procedure: patient.procedure,
          pointOfContact: inpatientForm.pointOfContact || teamInfo.resident || teamInfo.attending || 'Unknown',
          notes: inpatientForm.notes
      };

      await dbService.saveInpatient(newPatient);
      setInpatientSuccess(true);
      setTimeout(() => setInpatientSuccess(false), 3000);
      setInpatientForm(prev => ({...prev, active: false}));
  };

  const handleAddToCaseLog = async () => {
      if (!patient.name || !patient.procedure || !teamInfo.attending || !teamInfo.resident) {
          alert("Please complete the surgical team and patient info above first.");
          return;
      }
      if (!patient.caseNumber) {
          alert("Case number is required. Please enter it in the Operative Details section above.");
          return;
      }

      const newLog: SurgicalLog = {
          id: `sl-${Date.now()}`,
          date: teamInfo.date,
          hospital: caseLogForm.hospital,
          patientName: patient.name,
          caseNumber: patient.caseNumber,
          attending: teamInfo.attending,
          residentOne: teamInfo.resident,
          residentTwo: teamInfo.secondResident,
          procedure: patient.procedure,
          residentOneCertLogged: caseLogForm.res1Cert,
          residentTwoCertLogged: caseLogForm.res2Cert,
          timestamp: new Date().toISOString()
      };

      await dbService.saveSurgicalLog(newLog);
      setCaseLogSuccess(true);
      setTimeout(() => setCaseLogSuccess(false), 3000);
      setCaseLogForm(prev => ({...prev, active: false}));
  };

  const handleAddToOnCall = async () => {
      if (!patient.name || !patient.procedure) {
          alert("Please complete the patient info above first.");
          return;
      }
      if (!patient.caseNumber) {
          alert("Case number is required. Please enter it in the Operative Details section above.");
          return;
      }

      const nameParts = patient.name.trim().split(/\s+/);
      let lastName = '';
      let firstName = '';
      
      if (nameParts.length > 1) {
          lastName = nameParts.pop() || '';
          firstName = nameParts.join(' ');
      } else {
          firstName = nameParts[0] || '';
          lastName = 'Unknown'; 
      }

      const newOnCall: OnCallCaseLog = {
          id: `log-${Date.now()}`,
          date: teamInfo.date,
          location: onCallForm.location,
          patientFirstName: firstName,
          patientLastName: lastName,
          caseNumber: patient.caseNumber,
          referringErDoctor: onCallForm.referringDoctor,
          attending: toTitleCase(teamInfo.attending),
          resident: toTitleCase(teamInfo.resident),
          anesthesiaSupport: toTitleCase(teamInfo.anesthesia),
          procedure: patient.procedure,
          payrollCompleted: false
      };

      await dbService.saveOnCallLog(newOnCall);
      setOnCallSuccess(true);
      setTimeout(() => setOnCallSuccess(false), 3000);
      setOnCallForm(prev => ({...prev, active: false}));
  };

  return (
    <div className="h-full flex flex-col xl:flex-row p-6 gap-6 overflow-hidden bg-slate-50 dark:bg-[#0f172a] text-slate-900 dark:text-slate-100 transition-colors">
      {/* Input Form */}
      <div className="w-full xl:w-1/2 flex flex-col bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
        <div className="p-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 flex justify-between items-center">
            <h2 className="text-lg font-bold text-slate-800 dark:text-white flex items-center gap-2 brand-font">
                <FileText className="w-5 h-5 text-blue-900 dark:text-blue-400" />
                Operative Details
            </h2>
        </div>
        
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
            
            {/* Section 0: Surgical Team */}
            <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-xl border border-blue-100 dark:border-blue-900/50 space-y-4">
                <h3 className="text-sm font-bold text-blue-900 dark:text-blue-300 flex items-center gap-2 uppercase tracking-wide">
                    <Users className="w-4 h-4" /> Surgical Team
                </h3>
                <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                        <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase">Date</label>
                        <input type="date" className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded focus:ring-2 focus:ring-blue-900 outline-none text-slate-900 dark:text-white bg-white dark:bg-slate-900" 
                            value={teamInfo.date} onChange={e => setTeamInfo({...teamInfo, date: e.target.value})} />
                    </div>
                    <div className="space-y-1">
                        <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase">Anesthesia</label>
                        <input type="text" placeholder="e.g. Jess" className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded focus:ring-2 focus:ring-blue-900 outline-none text-slate-900 dark:text-white bg-white dark:bg-slate-900" 
                            value={teamInfo.anesthesia} onChange={e => setTeamInfo({...teamInfo, anesthesia: e.target.value})} />
                    </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                        <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase">Attending</label>
                        <select className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded focus:ring-2 focus:ring-blue-900 outline-none bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                            value={teamInfo.attending} onChange={e => setTeamInfo({...teamInfo, attending: e.target.value})}>
                            <option value="">-- Select --</option>
                            {attendings.map(name => <option key={name} value={name}>{name}</option>)}
                        </select>
                    </div>
                    <div className="space-y-1">
                        <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase">Primary Resident</label>
                        <select className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded focus:ring-2 focus:ring-blue-900 outline-none bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                            value={teamInfo.resident} 
                            onChange={e => {
                                const newResident = e.target.value;
                                setTeamInfo({...teamInfo, resident: newResident});
                                setInpatientForm(prev => ({ ...prev, pointOfContact: newResident }));
                            }}
                        >
                            <option value="">-- Select --</option>
                            {residents.map(name => <option key={name} value={name}>{name}</option>)}
                        </select>
                    </div>
                </div>
                <div className="space-y-1">
                    <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase">Second Resident (Optional)</label>
                    <select className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded focus:ring-2 focus:ring-blue-900 outline-none bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                        value={teamInfo.secondResident} onChange={e => setTeamInfo({...teamInfo, secondResident: e.target.value})}>
                        <option value="">-- None --</option>
                        {residents.map(name => <option key={name} value={name}>{name}</option>)}
                    </select>
                </div>
            </div>

            {/* Section 1: Signalment */}
            <div className="bg-slate-50 dark:bg-slate-900/50 p-4 rounded-xl border border-slate-200 dark:border-slate-700 space-y-4">
                <h3 className="text-sm font-bold text-slate-700 dark:text-slate-300 flex items-center gap-2 uppercase tracking-wide">
                    <Stethoscope className="w-4 h-4" /> Signalment
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1">
                        <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase">Patient Name</label>
                        <input type="text" className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded focus:ring-2 focus:ring-blue-900 outline-none text-slate-900 dark:text-white bg-white dark:bg-slate-900" 
                            value={patient.name} onChange={e => setPatient({...patient, name: e.target.value})} />
                    </div>
                    <div className="space-y-1">
                        <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase flex items-center gap-1"><Hash className="w-3 h-3" /> Case Number</label>
                        <input type="text" placeholder="e.g. 12345" className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded focus:ring-2 focus:ring-blue-900 outline-none text-slate-900 dark:text-white bg-white dark:bg-slate-900" 
                            value={patient.caseNumber} onChange={e => setPatient({...patient, caseNumber: e.target.value})} />
                    </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="space-y-1">
                        <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase">Weight (kg)</label>
                        <input type="number" className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded outline-none text-slate-900 dark:text-white bg-white dark:bg-slate-900" 
                            value={patient.weight} onChange={e => setPatient({...patient, weight: parseFloat(e.target.value) || 0})} />
                    </div>
                    <div className="space-y-1">
                        <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase">Species</label>
                        <select className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded outline-none bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                            value={patient.species} onChange={(e: any) => setPatient({...patient, species: e.target.value})}>
                            <option>Canine</option>
                            <option>Feline</option>
                            <option>Other</option>
                        </select>
                    </div>
                     <div className="space-y-1">
                        <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase">Sex</label>
                        <select className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded outline-none bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                            value={patient.sex} onChange={(e: any) => setPatient({...patient, sex: e.target.value})}>
                            <option>MN</option>
                            <option>FS</option>
                            <option>M</option>
                            <option>F</option>
                        </select>
                    </div>
                    <div className="space-y-1">
                        <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase">Breed/Age</label>
                        <input type="text" placeholder="e.g. 8y Lab" className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded outline-none text-slate-900 dark:text-white bg-white dark:bg-slate-900" 
                            value={patient.age} onChange={e => setPatient({...patient, age: e.target.value})} />
                    </div>
                </div>
            </div>

            <div className="space-y-4">
                 <div className="space-y-1">
                    <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase flex items-center gap-1">
                        Procedure Performed <Search className="w-3 h-3" />
                    </label>
                    <input 
                        list="procedures-list"
                        type="text" 
                        placeholder="e.g. Tibial Plateau Leveling Osteotomy (TPLO)" 
                        className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded focus:ring-2 focus:ring-blue-900 outline-none text-slate-900 dark:text-white bg-white dark:bg-slate-900" 
                        value={patient.procedure} 
                        onChange={e => setPatient({...patient, procedure: e.target.value})} 
                    />
                    <datalist id="procedures-list">
                        {PROCEDURES_LIST.map((p, idx) => (
                            <option key={idx} value={p} />
                        ))}
                    </datalist>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                     <div className="space-y-1">
                        <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase">Pre-Op Diagnosis</label>
                        <input type="text" placeholder="e.g. CrCL Rupture" className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded outline-none text-slate-900 dark:text-white bg-white dark:bg-slate-900" 
                            value={patient.preOpDiagnosis} onChange={e => setPatient({...patient, preOpDiagnosis: e.target.value})} />
                    </div>
                     <div className="space-y-1">
                        <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase">Post-Op Diagnosis</label>
                        <input type="text" placeholder="e.g. Complete CrCL Rupture, Medial Meniscal Tear" className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded outline-none text-slate-900 dark:text-white bg-white dark:bg-slate-900" 
                            value={patient.postOpDiagnosis} onChange={e => setPatient({...patient, postOpDiagnosis: e.target.value})} />
                    </div>
                </div>
            </div>

            <div className="space-y-4 border-t border-slate-200 dark:border-slate-700 pt-4">
                <div className="space-y-1">
                    <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase">Surgical Findings</label>
                    <textarea 
                        className="w-full p-3 border border-slate-300 dark:border-slate-600 rounded h-24 focus:ring-2 focus:ring-blue-900 outline-none resize-none text-sm text-slate-900 dark:text-white bg-white dark:bg-slate-900"
                        placeholder="Describe specific pathology, gross appearance, joint stability, osteophytes, etc."
                        value={surgicalDetails.findings}
                        onChange={e => setSurgicalDetails({...surgicalDetails, findings: e.target.value})}
                    ></textarea>
                </div>

                <div className="space-y-1">
                    <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase">Implants / Materials</label>
                    <textarea 
                        className="w-full p-3 border border-slate-300 dark:border-slate-600 rounded h-20 focus:ring-2 focus:ring-blue-900 outline-none resize-none text-sm text-slate-900 dark:text-white bg-white dark:bg-slate-900"
                        placeholder="e.g. 3.5mm LCP plate, 6 screws (sizes), PDS 2-0 suture..."
                        value={surgicalDetails.implants}
                        onChange={e => setSurgicalDetails({...surgicalDetails, implants: e.target.value})}
                    ></textarea>
                </div>

                <div className="grid grid-cols-2 gap-4">
                     <div className="space-y-1">
                        <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase">Closure</label>
                        <textarea 
                            className="w-full p-3 border border-slate-300 dark:border-slate-600 rounded h-24 focus:ring-2 focus:ring-blue-900 outline-none resize-none text-sm text-slate-900 dark:text-white bg-white dark:bg-slate-900"
                            placeholder="Routine three layer closure (include suture types)..."
                            value={surgicalDetails.closure}
                            onChange={e => setSurgicalDetails({...surgicalDetails, closure: e.target.value})}
                        ></textarea>
                    </div>
                     <div className="space-y-1">
                        <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase">Complications</label>
                        <textarea 
                            className="w-full p-3 border border-slate-300 dark:border-slate-600 rounded h-24 focus:ring-2 focus:ring-blue-900 outline-none resize-none text-sm text-slate-900 dark:text-white bg-white dark:bg-slate-900"
                            placeholder="Hemorrhage, hypotension, none..."
                            value={surgicalDetails.complications}
                            onChange={e => setSurgicalDetails({...surgicalDetails, complications: e.target.value})}
                        ></textarea>
                    </div>
                </div>
            </div>

            <div className="space-y-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                <div className={`bg-slate-50 dark:bg-slate-900/50 rounded-xl border transition-all duration-300 ${caseLogForm.active ? 'border-indigo-200 dark:border-indigo-500/50 ring-2 ring-indigo-50 dark:ring-indigo-900/20 shadow-md' : 'border-slate-200 dark:border-slate-700'}`}>
                    <div onClick={() => setCaseLogForm({...caseLogForm, active: !caseLogForm.active})} className="p-4 flex justify-between items-center cursor-pointer select-none">
                        <div className="flex items-center gap-2">
                             <div className={`p-1.5 rounded-lg ${caseLogForm.active ? 'bg-indigo-600 text-white' : 'bg-slate-200 dark:bg-slate-800 text-slate-500 dark:text-slate-400'}`}>
                                <Activity className="w-4 h-4" />
                            </div>
                            <h3 className={`text-sm font-bold uppercase tracking-wide ${caseLogForm.active ? 'text-indigo-900 dark:text-indigo-300' : 'text-slate-500 dark:text-slate-400'}`}>
                                Add to Surgical Case Log
                            </h3>
                        </div>
                        <div className="text-xs font-bold text-slate-400 uppercase">
                            {caseLogSuccess ? <span className="text-green-600 dark:text-green-400 flex items-center gap-1"><Check className="w-3 h-3" /> Added</span> : (caseLogForm.active ? 'Active' : 'Optional')}
                        </div>
                    </div>

                    {caseLogForm.active && (
                        <div className="p-4 pt-0 space-y-4 animate-in slide-in-from-top-2 duration-200">
                             <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-1">
                                    <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase">Hospital</label>
                                    <div className="flex gap-2">
                                        <button onClick={() => setCaseLogForm({...caseLogForm, hospital: 'MVES'})} className={`flex-1 py-1.5 text-xs font-bold rounded border ${caseLogForm.hospital === 'MVES' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-300 dark:border-slate-600'}`}>MVES</button>
                                        <button onClick={() => setCaseLogForm({...caseLogForm, hospital: 'OPVES'})} className={`flex-1 py-1.5 text-xs font-bold rounded border ${caseLogForm.hospital === 'OPVES' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-300 dark:border-slate-600'}`}>OPVES</button>
                                    </div>
                                </div>
                                <div className="space-y-1">
                                    <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase">Case Number</label>
                                    <div className="p-2 border border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-950 rounded text-sm text-slate-600 dark:text-slate-400 font-mono italic">
                                        {patient.caseNumber || 'Enter above in Operative Details'}
                                    </div>
                                </div>
                            </div>
                            
                            <div className="flex gap-4">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input type="checkbox" checked={caseLogForm.res1Cert} onChange={e => setCaseLogForm({...caseLogForm, res1Cert: e.target.checked})} className="rounded text-indigo-600 focus:ring-indigo-500 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600" />
                                    <span className="text-xs font-bold text-slate-600 dark:text-slate-300">Res 1 CERT</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input type="checkbox" checked={caseLogForm.res2Cert} onChange={e => setCaseLogForm({...caseLogForm, res2Cert: e.target.checked})} className="rounded text-indigo-600 focus:ring-indigo-500 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600" />
                                    <span className="text-xs font-bold text-slate-600 dark:text-slate-300">Res 2 CERT</span>
                                </label>
                            </div>

                            <button onClick={handleAddToCaseLog} disabled={!patient.caseNumber} className={`w-full py-2 rounded-lg font-bold flex items-center justify-center gap-2 transition-all text-sm ${caseLogSuccess ? 'bg-green-600 text-white' : 'bg-indigo-600 hover:bg-indigo-700 text-white'} disabled:opacity-50 disabled:cursor-not-allowed`}>
                                {caseLogSuccess ? <Check className="w-4 h-4" /> : <Save className="w-4 h-4" />}
                                {caseLogSuccess ? 'Logged' : 'Save to Resident Log'}
                            </button>
                        </div>
                    )}
                </div>

                <div className={`bg-slate-50 dark:bg-slate-900/50 rounded-xl border transition-all duration-300 ${onCallForm.active ? 'border-indigo-200 dark:border-indigo-500/50 ring-2 ring-indigo-50 dark:ring-indigo-900/20 shadow-md' : 'border-slate-200 dark:border-slate-700'}`}>
                    <div onClick={() => setOnCallForm({...onCallForm, active: !onCallForm.active})} className="p-4 flex justify-between items-center cursor-pointer select-none">
                        <div className="flex items-center gap-2">
                             <div className={`p-1.5 rounded-lg ${onCallForm.active ? 'bg-indigo-600 text-white' : 'bg-slate-200 dark:bg-slate-800 text-slate-500 dark:text-slate-400'}`}>
                                <ClipboardCheck className="w-4 h-4" />
                            </div>
                            <h3 className={`text-sm font-bold uppercase tracking-wide ${onCallForm.active ? 'text-indigo-900 dark:text-indigo-300' : 'text-slate-500 dark:text-slate-400'}`}>
                                Add to On-Call Tracker
                            </h3>
                        </div>
                        <div className="text-xs font-bold text-slate-400 uppercase">
                            {onCallSuccess ? <span className="text-green-600 dark:text-green-400 flex items-center gap-1"><Check className="w-3 h-3" /> Added</span> : (onCallForm.active ? 'Active' : 'Optional')}
                        </div>
                    </div>

                    {onCallForm.active && (
                        <div className="p-4 pt-0 space-y-4 animate-in slide-in-from-top-2 duration-200">
                             <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-1">
                                    <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase">Location</label>
                                    <div className="flex gap-2">
                                        <button onClick={() => setOnCallForm({...onCallForm, location: 'MVES'})} className={`flex-1 py-1.5 text-xs font-bold rounded border ${onCallForm.location === 'MVES' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-300 dark:border-slate-600'}`}>MVES</button>
                                        <button onClick={() => setOnCallForm({...onCallForm, location: 'OPVES'})} className={`flex-1 py-1.5 text-xs font-bold rounded border ${onCallForm.location === 'OPVES' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-300 dark:border-slate-600'}`}>OPVES</button>
                                    </div>
                                </div>
                                <div className="space-y-1">
                                    <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase">Case Number</label>
                                    <div className="p-2 border border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-950 rounded text-sm text-slate-600 dark:text-slate-400 font-mono italic">
                                        {patient.caseNumber || 'Enter above in Operative Details'}
                                    </div>
                                </div>
                            </div>
                            
                            <div className="space-y-1">
                                <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase">Referring Doctor</label>
                                <input type="text" className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded focus:ring-2 focus:ring-indigo-500 outline-none text-slate-900 dark:text-white bg-white dark:bg-slate-900 text-sm" placeholder="e.g. Dr. Smith" value={onCallForm.referringDoctor} onChange={e => setOnCallForm({...onCallForm, referringDoctor: e.target.value})} />
                            </div>

                            <button onClick={handleAddToOnCall} disabled={!patient.caseNumber} className={`w-full py-2 rounded-lg font-bold flex items-center justify-center gap-2 transition-all text-sm ${onCallSuccess ? 'bg-green-600 text-white' : 'bg-indigo-600 hover:bg-indigo-700 text-white'} disabled:opacity-50 disabled:cursor-not-allowed`}>
                                {onCallSuccess ? <Check className="w-4 h-4" /> : <Save className="w-4 h-4" />}
                                {onCallSuccess ? 'Added' : 'Save to On-Call Tracker'}
                            </button>
                        </div>
                    )}
                </div>

                <div className={`bg-slate-50 dark:bg-slate-900/50 rounded-xl border transition-all duration-300 ${inpatientForm.active ? 'border-indigo-200 dark:border-indigo-500/50 ring-2 ring-indigo-50 dark:ring-indigo-900/20 shadow-md' : 'border-slate-200 dark:border-slate-700'}`}>
                    <div onClick={() => setInpatientForm({...inpatientForm, active: !inpatientForm.active})} className="p-4 flex justify-between items-center cursor-pointer select-none">
                        <div className="flex items-center gap-2">
                             <div className={`p-1.5 rounded-lg ${inpatientForm.active ? 'bg-indigo-600 text-white' : 'bg-slate-200 dark:bg-slate-800 text-slate-500 dark:text-slate-400'}`}>
                                <BedDouble className="w-4 h-4" />
                            </div>
                            <h3 className={`text-sm font-bold uppercase tracking-wide ${inpatientForm.active ? 'text-indigo-900 dark:text-indigo-300' : 'text-slate-500 dark:text-slate-400'}`}>
                                Add to Inpatient Census
                            </h3>
                        </div>
                        <div className="text-xs font-bold text-slate-400 uppercase">
                            {inpatientSuccess ? <span className="text-green-600 dark:text-green-400 flex items-center gap-1"><Check className="w-3 h-3" /> Added</span> : (inpatientForm.active ? 'Active' : 'Optional')}
                        </div>
                    </div>

                    {inpatientForm.active && (
                        <div className="p-4 pt-0 space-y-4 animate-in slide-in-from-top-2 duration-200">
                             <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-1">
                                    <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase">Hospital</label>
                                    <div className="flex gap-2">
                                        <button onClick={() => setInpatientForm({...inpatientForm, location: 'Mission'})} className={`flex-1 py-1.5 text-xs font-bold rounded border ${inpatientForm.location === 'Mission' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-300 dark:border-slate-600'}`}>Mission</button>
                                        <button onClick={() => setInpatientForm({...inpatientForm, location: 'OP'})} className={`flex-1 py-1.5 text-xs font-bold rounded border ${inpatientForm.location === 'OP' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-300 dark:border-slate-600'}`}>OP</button>
                                    </div>
                                </div>
                                <div className="space-y-1">
                                    <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase">Point of Contact</label>
                                    <input type="text" className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded focus:ring-2 focus:ring-indigo-500 outline-none text-slate-900 dark:text-white bg-white dark:bg-slate-900 text-sm" placeholder="Doctor/Tech" value={inpatientForm.pointOfContact} onChange={e => setInpatientForm({...inpatientForm, pointOfContact: e.target.value})} />
                                </div>
                            </div>
                            
                            <div className="space-y-1">
                                <div className="flex justify-between items-center mb-1">
                                    <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase">Status Notes</label>
                                    <button onClick={handleAutopopulateStatus} disabled={isGeneratingNotes || !patient.procedure} className="flex items-center gap-1.5 px-2 py-0.5 rounded bg-indigo-50 dark:bg-indigo-900/40 text-[10px] font-bold text-indigo-600 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800 hover:bg-indigo-100 disabled:opacity-50">
                                        {isGeneratingNotes ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <Sparkles className="w-2.5 h-2.5" />}
                                        AI Post-Op Plan
                                    </button>
                                </div>
                                <textarea className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded focus:ring-2 focus:ring-indigo-500 outline-none text-slate-900 dark:text-white bg-white dark:bg-slate-900 text-sm h-32 resize-none" value={inpatientForm.notes} onChange={e => setInpatientForm({...inpatientForm, notes: e.target.value})} placeholder="Click AI Post-Op Plan to generate specific monitoring and meds based on this case." />
                            </div>

                            <button onClick={handleAddToInpatient} className={`w-full py-2 rounded-lg font-bold flex items-center justify-center gap-2 transition-all text-sm ${inpatientSuccess ? 'bg-green-600 text-white' : 'bg-indigo-600 hover:bg-indigo-700 text-white'}`}>
                                {inpatientSuccess ? <Check className="w-4 h-4" /> : <Save className="w-4 h-4" />}
                                {inpatientSuccess ? 'Added' : 'Save to Census'}
                            </button>
                        </div>
                    )}
                </div>

                <div className={`bg-slate-50 dark:bg-slate-900/50 rounded-xl border transition-all duration-300 ${labForm.active ? 'border-indigo-200 dark:border-indigo-500/50 ring-2 ring-indigo-50 dark:ring-indigo-900/20 shadow-md' : 'border-slate-200 dark:border-slate-700'}`}>
                    <div onClick={() => setLabForm({...labForm, active: !labForm.active})} className="p-4 flex justify-between items-center cursor-pointer select-none">
                        <div className="flex items-center gap-2">
                             <div className={`p-1.5 rounded-lg ${labForm.active ? 'bg-indigo-600 text-white' : 'bg-slate-200 dark:bg-slate-800 text-slate-500 dark:text-slate-400'}`}>
                                <FlaskConical className="w-4 h-4" />
                            </div>
                            <h3 className={`text-sm font-bold uppercase tracking-wide ${labForm.active ? 'text-indigo-900 dark:text-indigo-300' : 'text-slate-500 dark:text-slate-400'}`}>
                                Add to Lab Tracker
                            </h3>
                        </div>
                        <div className="text-xs font-bold text-slate-400 uppercase">
                            {labSuccess ? <span className="text-green-600 dark:text-green-400 flex items-center gap-1"><Check className="w-3 h-3" /> Added</span> : (labForm.active ? 'Active' : 'Optional')}
                        </div>
                    </div>

                    {labForm.active && (
                        <div className="p-4 pt-0 space-y-4 animate-in slide-in-from-top-2 duration-200">
                             <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-1">
                                    <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase">Sample Type</label>
                                    <input type="text" className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded focus:ring-2 focus:ring-indigo-500 outline-none text-slate-900 dark:text-white bg-white dark:bg-slate-900 text-sm" value={labForm.sampleType} onChange={e => setLabForm({...labForm, sampleType: e.target.value})} />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase">Submitted To</label>
                                    <input type="text" className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded focus:ring-2 focus:ring-indigo-500 outline-none text-slate-900 dark:text-white bg-white dark:bg-slate-900 text-sm" value={labForm.submittedTo} onChange={e => setLabForm({...labForm, submittedTo: e.target.value})} />
                                </div>
                            </div>
                            
                            <div className="space-y-1">
                                <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase">Hospital</label>
                                <div className="flex gap-2">
                                    <button onClick={() => setLabForm({...labForm, hospital: 'Mission'})} className={`flex-1 py-1.5 text-xs font-bold rounded border ${labForm.hospital === 'Mission' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-300 dark:border-slate-600'}`}>Mission</button>
                                    <button onClick={() => setLabForm({...labForm, hospital: 'OP'})} className={`flex-1 py-1.5 text-xs font-bold rounded border ${labForm.hospital === 'OP' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-300 dark:border-slate-600'}`}>OP</button>
                                </div>
                            </div>

                            <button onClick={handleAddToLab} className={`w-full py-2 rounded-lg font-bold flex items-center justify-center gap-2 transition-all text-sm ${labSuccess ? 'bg-green-600 text-white' : 'bg-indigo-600 hover:bg-indigo-700 text-white'}`}>
                                {labSuccess ? <Check className="w-4 h-4" /> : <Save className="w-4 h-4" />}
                                {labSuccess ? 'Added' : 'Save to Lab Tracker'}
                            </button>
                        </div>
                    )}
                </div>

            </div>

            <button onClick={handleGenerate} disabled={loading} className="w-full py-4 mt-6 bg-gradient-to-r from-blue-700 to-indigo-700 text-white font-bold rounded-xl shadow-xl hover:shadow-2xl hover:scale-[1.01] transition-all flex items-center justify-center gap-3 text-lg disabled:opacity-70 disabled:cursor-not-allowed group">
                {loading ? <Loader2 className="w-6 h-6 animate-spin" /> : <Wand2 className="w-6 h-6 group-hover:rotate-12 transition-transform" />}
                {loading ? 'Generating Report...' : 'Generate Operative Report'}
            </button>
        </div>
      </div>

      <div className="w-full xl:w-1/2 flex flex-col bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden h-[600px] xl:h-auto">
        <div className="p-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 flex justify-between items-center">
            <h2 className="text-lg font-bold text-slate-800 dark:text-white flex items-center gap-2 brand-font">
                <BrainCircuit className="w-5 h-5 text-indigo-500" />
                Generated Report
            </h2>
            <div className="flex gap-2">
                {learningSuccess && (
                    <span className="text-xs font-bold text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/30 px-3 py-1.5 rounded-lg border border-green-200 dark:border-green-800 animate-in fade-in flex items-center gap-1">
                        <Check className="w-3 h-3" /> Learning Saved
                    </span>
                )}
                <button onClick={handleCopyAndSave} disabled={!generatedReport} className={`px-4 py-1.5 rounded-lg text-sm font-bold flex items-center gap-2 transition-all ${copied ? 'bg-green-600 text-white' : 'bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-300 dark:hover:bg-slate-700 disabled:opacity-50'}`}>
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    {copied ? 'Copied' : 'Copy & Save Learning'}
                </button>
            </div>
        </div>
        
        <div className="flex-1 overflow-y-auto p-0 bg-slate-50 dark:bg-slate-950 font-mono text-sm relative flex flex-col">
            {loading ? (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/50 dark:bg-slate-900/50 backdrop-blur-sm z-10">
                    <Loader2 className="w-12 h-12 text-indigo-600 animate-spin mb-4" />
                    <p className="font-bold text-slate-600 dark:text-slate-300 animate-pulse">Consulting surgical knowledge base...</p>
                </div>
            ) : generatedReport ? (
                <textarea className="w-full h-full p-8 bg-transparent border-none outline-none resize-none whitespace-pre-wrap leading-relaxed text-slate-800 dark:text-slate-300 selection:bg-indigo-100 dark:selection:bg-indigo-900" value={generatedReport} onChange={(e) => setGeneratedReport(e.target.value)} spellCheck={false} />
            ) : (
                <div className="h-full flex flex-col items-center justify-center text-slate-400 opacity-60 p-8">
                    <FileText className="w-16 h-16 mb-4" />
                    <p className="text-lg font-bold">Ready to Generate</p>
                    <p className="text-sm">Fill in the details on the left and click Generate.</p>
                </div>
            )}
        </div>
      </div>
    </div>
  );
};
